<template>
  <div class="page payment-methods-page">
    <div class="page-header">
      <div class="page-header-row">
        <div>
          <h1 class="page-title">Formas de pagamento</h1>
          <p class="page-subtitle">Configure métodos de pagamento e taxas de processamento</p>
        </div>
        <button
          v-if="sortedMethods.length > 1"
          type="button"
          class="btn btn-secondary btn-reorder"
          @click="openReorder"
        >
          <i class="material-icons">swap_vert</i>
          Reordenar
        </button>
      </div>
    </div>

    <div class="card-list">
      <article
        v-for="method in sortedMethods"
        :key="method.id"
        class="card card--with-icon"
      >
        <div class="card-icon-wrap payment-methods-card-icon">
          <i class="material-icons">credit_card</i>
        </div>
        <div class="card-content">
          <h3 class="card-title card-primary">
            {{ method.name }}
            <span v-if="method.isDefault" class="card-badge card-badge-inline">
              <i class="material-icons">star</i>
              Padrão
            </span>
          </h3>
          <p class="card-meta card-accent">
            <i class="material-icons card-meta-icon">percent</i>
            Taxa: {{ formatFeePercent(method.feePercent) }}%
          </p>
        </div>
        <div class="card-actions">
          <button
            type="button"
            class="btn-icon"
            aria-label="Editar"
            @click="openEdit(method)"
          >
            <i class="material-icons">edit</i>
          </button>
          <button
            type="button"
            class="btn-icon btn-icon-danger"
            aria-label="Excluir"
            @click="handleDeleteClick(method)"
          >
            <i class="material-icons">delete_outline</i>
          </button>
        </div>
      </article>
    </div>

    <p v-if="sortedMethods.length === 0" class="empty-hint">
      Nenhuma forma de pagamento cadastrada. Adicione a primeira.
    </p>

    <button
      type="button"
      class="btn-fab"
      @click="openAdd"
      aria-label="Nova forma de pagamento"
    >
      <i class="material-icons">add</i>
    </button>

    <Drawer :model-value="showModal" @update:model-value="showModal = $event">
      <div class="modal-header">
        <h2 class="modal-title">{{ editingId ? 'Alterar forma de pagamento' : 'Nova forma de pagamento' }}</h2>
        <button
          type="button"
          class="modal-close"
          aria-label="Fechar"
          @click="showModal = false"
        >
          <i class="material-icons">close</i>
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="save">
          <div class="form-group">
            <label for="method-name">Nome</label>
            <input
              id="method-name"
              type="text"
              v-model="methodName"
              placeholder="Ex: Cartão de crédito, PIX, Dinheiro"
              :aria-invalid="!!nameError"
            />
            <p v-if="nameError" class="field-error" role="alert">{{ nameError }}</p>
          </div>
          <div class="form-group">
            <label for="method-fee">Taxa (%)</label>
            <input
              id="method-fee"
              type="number"
              v-model="feePercentInput"
              min="0"
              max="100"
              step="0.01"
              placeholder="0,00"
              :aria-invalid="!!feeError"
            />
            <p class="field-hint">Percentual perdido no processamento (ex: 3,50 para cartão). Até 2 casas decimais.</p>
            <p v-if="feeError" class="field-error" role="alert">{{ feeError }}</p>
          </div>
          <div class="form-group form-group--checkbox">
            <label class="checkbox-label">
              <input type="checkbox" v-model="isDefault" />
              <span>Usar como forma de pagamento padrão</span>
            </label>
            <p class="field-hint">Usada automaticamente quando nenhuma forma é selecionada.</p>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" :disabled="saveLoading" @click="showModal = false">
          Cancelar
        </button>
        <button type="button" class="btn btn-primary" :disabled="saveLoading" @click="save">
          {{ saveLoading ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </Drawer>

    <ConfirmDialog
      v-model="showDeleteConfirm"
      title="Excluir forma de pagamento"
      :message="deleteMessage"
      confirm-label="Excluir"
      :confirm-loading="deleteLoading"
      @confirm="doDelete"
    />

    <Drawer :model-value="showReorder" @update:model-value="showReorder = $event">
      <div class="modal-header">
        <h2 class="modal-title">Reordenar</h2>
        <button
          type="button"
          class="modal-close"
          aria-label="Fechar"
          @click="showReorder = false"
        >
          <i class="material-icons">close</i>
        </button>
      </div>
      <div class="modal-body reorder-body">
        <p class="reorder-hint">
          Arraste para reordenar ou use as setas. A ordem reflete nos seletores de atendimento.
        </p>
        <draggable
          :list="reorderList"
          item-key="id"
          handle=".reorder-handle"
          ghost-class="reorder-ghost"
          :animation="200"
        >
          <template #item="{ element: method, index: idx }">
            <div class="reorder-item">
              <button
                type="button"
                class="reorder-handle"
                aria-label="Arrastar"
              >
                <i class="material-icons">drag_indicator</i>
              </button>
              <span class="reorder-item-name">{{ method.name }}</span>
              <span class="reorder-item-fee">{{ formatFeePercent(method.feePercent) }}%</span>
              <div class="reorder-arrows">
                <button
                  type="button"
                  class="btn-icon btn-icon-sm"
                  :disabled="idx === 0"
                  aria-label="Mover para cima"
                  @click="moveUp(idx)"
                >
                  <i class="material-icons">arrow_upward</i>
                </button>
                <button
                  type="button"
                  class="btn-icon btn-icon-sm"
                  :disabled="idx === reorderList.length - 1"
                  aria-label="Mover para baixo"
                  @click="moveDown(idx)"
                >
                  <i class="material-icons">arrow_downward</i>
                </button>
              </div>
            </div>
          </template>
        </draggable>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" :disabled="reorderLoading" @click="showReorder = false">
          Cancelar
        </button>
        <button type="button" class="btn btn-primary" :disabled="reorderLoading" @click="saveOrder">
          {{ reorderLoading ? 'Salvando...' : 'Salvar ordem' }}
        </button>
      </div>
    </Drawer>
  </div>
</template>

<script>
import Drawer from '@/components/Drawer.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import draggable from 'vuedraggable';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  limit,
  writeBatch,
} from 'firebase/firestore';
import { firestore } from '@/firebase';
import { ref, computed } from 'vue';
import { useToast } from '@/composables/useToast';
import { usePaymentMethodStore } from '@/stores/paymentMethod';
import { formatFeePercent, isValidFeePercent } from '@/composables/usePaymentMethodCalculations';

export default {
  name: 'PaymentMethods',
  components: { Drawer, ConfirmDialog, draggable },
  setup() {
    const { show: showToast } = useToast();
    const paymentMethodStore = usePaymentMethodStore();

    const showModal = ref(false);
    const showDeleteConfirm = ref(false);
    const deleteTarget = ref(null);
    const saveLoading = ref(false);
    const deleteLoading = ref(false);
    const editingId = ref(null);
    const methodName = ref('');
    const feePercentInput = ref('0');
    const isDefault = ref(false);
    const nameError = ref('');
    const feeError = ref('');
    const deleteMessage = ref('');
    const showReorder = ref(false);
    const reorderList = ref([]);
    const reorderLoading = ref(false);

    const sortedMethods = computed(() => paymentMethodStore.allPaymentMethods);

    function openAdd() {
      editingId.value = null;
      methodName.value = '';
      feePercentInput.value = '0';
      isDefault.value = sortedMethods.value.length === 0;
      nameError.value = '';
      feeError.value = '';
      showModal.value = true;
    }

    function openEdit(method) {
      editingId.value = method.id;
      methodName.value = method.name || '';
      feePercentInput.value = String(method.feePercent ?? 0);
      isDefault.value = !!method.isDefault;
      nameError.value = '';
      feeError.value = '';
      showModal.value = true;
    }

    function validate() {
      nameError.value = '';
      feeError.value = '';
      let valid = true;
      if (!methodName.value || !methodName.value.trim()) {
        nameError.value = 'Informe o nome da forma de pagamento.';
        valid = false;
      }
      if (!isValidFeePercent(feePercentInput.value)) {
        feeError.value = 'Informe uma taxa entre 0 e 100 (até 2 casas decimais).';
        valid = false;
      }
      return valid;
    }

    async function clearOtherDefaults(exceptId) {
      const defaults = sortedMethods.value.filter((m) => m.isDefault && m.id !== exceptId);
      if (defaults.length === 0) return;
      const batch = writeBatch(firestore);
      for (const method of defaults) {
        batch.set(doc(firestore, 'paymentMethods', method.id), { isDefault: false }, { merge: true });
      }
      await batch.commit();
    }

    async function save() {
      if (!validate()) return;
      saveLoading.value = true;
      try {
        const id = editingId.value || doc(collection(firestore, 'paymentMethods')).id;
        const feePercent = Math.round(Number(feePercentInput.value) * 100) / 100;
        const payload = {
          name: methodName.value.trim(),
          feePercent,
          isDefault: !!isDefault.value,
          order: editingId.value
            ? (paymentMethodStore.paymentMethods[editingId.value]?.order ?? 0)
            : sortedMethods.value.length,
        };
        if (isDefault.value) {
          await clearOtherDefaults(id);
        } else if (!editingId.value && sortedMethods.value.length === 0) {
          payload.isDefault = true;
        }
        await setDoc(doc(firestore, 'paymentMethods', id), payload, { merge: true });
        showModal.value = false;
        showToast(editingId.value ? 'Forma de pagamento alterada.' : 'Forma de pagamento criada.', 'success');
      } catch (err) {
        showToast('Não foi possível salvar. Tente novamente.', 'error');
      } finally {
        saveLoading.value = false;
      }
    }

    async function handleDeleteClick(method) {
      deleteTarget.value = method;
      try {
        const usageQuery = query(
          collection(firestore, 'appointments'),
          where('paymentMethodId', '==', method.id),
          limit(1)
        );
        const usageSnap = await getDocs(usageQuery);
        if (!usageSnap.empty) {
          showToast(
            'Não é possível excluir: existem atendimentos usando esta forma de pagamento.',
            'error'
          );
          return;
        }
        deleteMessage.value = `Tem certeza que deseja excluir "${method.name}"?`;
        showDeleteConfirm.value = true;
      } catch (err) {
        showToast('Não foi possível verificar o uso desta forma de pagamento.', 'error');
      }
    }

    async function doDelete() {
      if (!deleteTarget.value) return;
      deleteLoading.value = true;
      try {
        const usageQuery = query(
          collection(firestore, 'appointments'),
          where('paymentMethodId', '==', deleteTarget.value.id),
          limit(1)
        );
        const usageSnap = await getDocs(usageQuery);
        if (!usageSnap.empty) {
          showToast(
            'Não é possível excluir: existem atendimentos usando esta forma de pagamento.',
            'error'
          );
          return;
        }
        const wasDefault = !!deleteTarget.value.isDefault;
        await deleteDoc(doc(firestore, 'paymentMethods', deleteTarget.value.id));
        if (wasDefault) {
          const remaining = sortedMethods.value.filter((m) => m.id !== deleteTarget.value.id);
          if (remaining.length > 0) {
            await setDoc(
              doc(firestore, 'paymentMethods', remaining[0].id),
              { isDefault: true },
              { merge: true }
            );
          }
        }
        showDeleteConfirm.value = false;
        deleteTarget.value = null;
        showToast('Forma de pagamento excluída.', 'success');
      } catch (err) {
        showToast('Não foi possível excluir. Tente novamente.', 'error');
      } finally {
        deleteLoading.value = false;
      }
    }

    function openReorder() {
      reorderList.value = sortedMethods.value.map((m) => ({
        id: m.id,
        name: m.name,
        feePercent: m.feePercent,
      }));
      showReorder.value = true;
    }

    function moveUp(idx) {
      if (idx <= 0) return;
      const list = reorderList.value;
      const item = list.splice(idx, 1)[0];
      list.splice(idx - 1, 0, item);
    }

    function moveDown(idx) {
      const list = reorderList.value;
      if (idx >= list.length - 1) return;
      const item = list.splice(idx, 1)[0];
      list.splice(idx + 1, 0, item);
    }

    async function saveOrder() {
      reorderLoading.value = true;
      try {
        const batch = writeBatch(firestore);
        reorderList.value.forEach((method, index) => {
          batch.set(doc(firestore, 'paymentMethods', method.id), { order: index }, { merge: true });
        });
        await batch.commit();
        showReorder.value = false;
        showToast('Ordem salva.', 'success');
      } catch (err) {
        showToast('Não foi possível salvar a ordem. Tente novamente.', 'error');
      } finally {
        reorderLoading.value = false;
      }
    }

    return {
      sortedMethods,
      showModal,
      showDeleteConfirm,
      saveLoading,
      deleteLoading,
      editingId,
      methodName,
      feePercentInput,
      isDefault,
      nameError,
      feeError,
      deleteMessage,
      openAdd,
      openEdit,
      save,
      handleDeleteClick,
      doDelete,
      formatFeePercent,
      showReorder,
      reorderList,
      reorderLoading,
      openReorder,
      moveUp,
      moveDown,
      saveOrder,
    };
  },
};
</script>

<style scoped>
.payment-methods-card-icon {
  background: var(--color-accent-soft);
  color: var(--color-accent);
}

.card-badge-inline {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: var(--space-xs);
  vertical-align: middle;
  font-size: 0.75rem;
}

.card-badge-inline .material-icons {
  font-size: 14px;
}

.empty-hint {
  text-align: center;
  color: var(--color-text-muted);
  margin: var(--space-xl) 0;
}

.field-error {
  font-size: 0.875rem;
  color: var(--color-error);
  margin-top: var(--space-xs);
}

.field-hint {
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin: var(--space-xs) 0 0;
}

.page-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-md);
  flex-wrap: wrap;
}

.btn-reorder {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  flex-shrink: 0;
}

.reorder-body {
  padding: var(--space-md) 0;
}

.reorder-hint {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  margin: 0 0 var(--space-md);
}

.reorder-item {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-sm);
  background: var(--color-accent-soft);
  border: 1px solid var(--color-border);
  margin-bottom: var(--space-xs);
}

.reorder-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: grab;
  color: var(--color-text-secondary);
  padding: 0;
}

.reorder-handle:active {
  cursor: grabbing;
}

.reorder-item-name {
  flex: 1;
  min-width: 0;
  font-weight: 500;
}

.reorder-item-fee {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  font-variant-numeric: tabular-nums;
}

.reorder-arrows {
  display: flex;
  gap: 2px;
}

.reorder-ghost {
  opacity: 0.5;
}
</style>
